import type { FC } from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import ErrorList from '@/components/ErrorList'
import H2 from '@/components/H2'
import TextInput from '@/components/input/TextInput'
import LoadingSpinner from '@/components/LoadingSpinner'
import ctx from '@/context/AppContext'
import { useVerifyOrganisationFinanceMutation } from '@/lib/apollo/mutation/verify-organisation-finance'
import { getErrorsFromGraphQLExtensions } from '@/lib/apollo/utils/getUpstreamErrors'
import type { ErrorContext } from '@/lib/apollo/utils/getUpstreamErrors'
import type { CompanyFinance } from '@/types/Activity'

const ContentContainer = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
`

export interface Props {
  customerId: string
  subjectId: string
  finance?: CompanyFinance
  onClose: () => void
}

interface Fields {
  assets: number
  liabilities: number
  unrestrictedFunds: number
  cashAndEquivalent: number
}

const VerifyOrganisationFinanceSidebar: FC<Props> = ({
  customerId,
  subjectId,
  finance,
  onClose,
}) => {
  const [errors, setErrors] = useState<ErrorContext<string>[] | undefined>()
  const [fields, setFields] = useState<Fields>({
    assets: finance?.assets ?? 0,
    cashAndEquivalent: finance?.cashAndEquivalent ?? 0,
    liabilities: finance?.liabilities ?? 0,
    unrestrictedFunds: finance?.unrestrictedFunds ?? 0,
  })

  const [verifyOrganisationFinance, { loading, error, data }] =
    useVerifyOrganisationFinanceMutation()

  const { personId } = useContext(ctx)

  const onConfirm = () => {
    verifyOrganisationFinance({
      variables: {
        customerId,
        subjectId,
        userId: personId ?? '',
        request: {
          assets: fields.assets,
          cashAndEquivalent: fields.cashAndEquivalent,
          liabilities: fields.liabilities,
          unrestrictedFunds: fields.unrestrictedFunds,
        },
      },
      onError: error => {
        setErrors(getErrorsFromGraphQLExtensions(error))
      },
    })
  }

  return (
    <SlideInSidebar
      onClose={onClose}
      header={<H2>Verify organisation finance</H2>}
      content={
        <ContentContainer>
          {data !== undefined || loading ? (
            <LoadingSpinner />
          ) : (
            <div>
              <TextInput
                name="assets"
                label="Assets"
                onChange={e => setFields({ ...fields, assets: +e.target.value })}
                type="number"
                value={fields.assets}
                required
              />
              <TextInput
                name="cash-and-equivalent"
                label="Cash and Equivalent"
                onChange={e =>
                  setFields({ ...fields, cashAndEquivalent: +e.target.value })
                }
                type="number"
                value={fields.cashAndEquivalent}
                required
              />
              <TextInput
                name="liabilities"
                label="Liabilities"
                onChange={e => setFields({ ...fields, liabilities: +e.target.value })}
                type="number"
                value={fields.liabilities}
                required
              />
              <TextInput
                name="unrestricted-funds"
                label="Unrestricted Funds"
                onChange={e =>
                  setFields({ ...fields, unrestrictedFunds: +e.target.value })
                }
                type="number"
                value={fields.unrestrictedFunds}
                required
              />
              {errors && <ErrorList errors={errors} />}
            </div>
          )}
        </ContentContainer>
      }
      footer={
        <>
          {error !== undefined && (
            <div>An unexpected error occurred. {error?.message}</div>
          )}
          <div className="side-by-side-flex-end">
            <AlternateButton onClick={onClose}>Cancel</AlternateButton>
            <Button disabled={loading} onClick={onConfirm}>
              Confirm
            </Button>
          </div>
        </>
      }
    />
  )
}

export default VerifyOrganisationFinanceSidebar
